/**
 * Selection Store
 * Manages multi-track selection state with keyboard support (Shift, Ctrl/Cmd)
 */

import { create } from 'zustand';

/**
 * Selection state interface
 */
export interface SelectionState {
  // Selected instance IDs (using Set for O(1) lookup)
  // Instance IDs uniquely identify individual track instances in the playlist
  selectedTrackIds: Set<string>;

  // Last selected instance ID (for range selection with Shift)
  // Instance ID uniquely identifies individual track instances in the playlist
  lastSelectedId: string | null;

  // Toggle single track instance selection
  // instanceId: Unique identifier for the track instance (not Spotify track ID)
  toggleSelection: (instanceId: string) => void;

  // Select only this track instance (clear others)
  // instanceId: Unique identifier for the track instance (not Spotify track ID)
  selectOnly: (instanceId: string) => void;

  // Select range of track instances (Shift+Click)
  // startId and endId: Unique identifiers for track instances (not Spotify track IDs)
  // allIds: Array of instance IDs in order
  selectRange: (startId: string, endId: string, allIds: string[]) => void;

  // Select all track instances
  // instanceIds: Array of unique identifiers for track instances (not Spotify track IDs)
  selectAll: (instanceIds: string[]) => void;

  // Clear all selections
  clearSelection: () => void;

  // Check if track instance is selected
  // instanceId: Unique identifier for the track instance (not Spotify track ID)
  isSelected: (instanceId: string) => boolean;

  // Get selection count
  getSelectionCount: () => number;

  // Get selected instance IDs as array
  getSelectedIds: () => string[];

  // Add track instances to selection without clearing
  // instanceIds: Array of unique identifiers for track instances (not Spotify track IDs)
  addToSelection: (instanceIds: string[]) => void;

  // Remove track instances from selection
  // instanceIds: Array of unique identifiers for track instances (not Spotify track IDs)
  removeFromSelection: (instanceIds: string[]) => void;

  // Invert selection across all track instances
  // allIds: Array of unique identifiers for track instances (not Spotify track IDs)
  invertSelection: (allIds: string[]) => void;
}

/**
 * Selection store
 */
export const useSelectionStore = create<SelectionState>((set, get) => ({
  // Initial state
  selectedTrackIds: new Set<string>(),
  lastSelectedId: null,

  // Toggle single track instance selection
  toggleSelection: (instanceId) => {
    const { selectedTrackIds } = get();
    const newSelection = new Set(selectedTrackIds);

    if (newSelection.has(instanceId)) {
      newSelection.delete(instanceId);
      // If we're deselecting the last selected instance, clear lastSelectedId
      set({
        selectedTrackIds: newSelection,
        lastSelectedId: newSelection.size > 0 ? instanceId : null,
      });
    } else {
      newSelection.add(instanceId);
      set({
        selectedTrackIds: newSelection,
        lastSelectedId: instanceId,
      });
    }
  },

  // Select only this track instance (clear others)
  selectOnly: (instanceId) => {
    set({
      selectedTrackIds: new Set([instanceId]),
      lastSelectedId: instanceId,
    });
  },

  // Select range of track instances (Shift+Click behavior)
  selectRange: (startId, endId, allIds) => {
    const { selectedTrackIds } = get();
    const newSelection = new Set(selectedTrackIds);

    // Find indices of start and end instances
    const startIndex = allIds.indexOf(startId);
    const endIndex = allIds.indexOf(endId);

    if (startIndex === -1 || endIndex === -1) {
      // If either instance not found, just select the endId
      newSelection.add(endId);
      set({
        selectedTrackIds: newSelection,
        lastSelectedId: endId,
      });
      return;
    }

    // Select all instances in range (inclusive)
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);

    for (let i = start; i <= end; i++) {
      const instanceId = allIds[i];
      if (instanceId) {
        newSelection.add(instanceId);
      }
    }

    set({
      selectedTrackIds: newSelection,
      lastSelectedId: endId,
    });
  },

  // Select all track instances
  selectAll: (instanceIds) => {
    set({
      selectedTrackIds: new Set(instanceIds),
      lastSelectedId: instanceIds[instanceIds.length - 1] ?? null,
    });
  },

  // Clear all selections
  clearSelection: () => {
    set({
      selectedTrackIds: new Set<string>(),
      lastSelectedId: null,
    });
  },

  // Check if track instance is selected
  isSelected: (instanceId) => {
    return get().selectedTrackIds.has(instanceId);
  },

  // Get selection count
  getSelectionCount: () => {
    return get().selectedTrackIds.size;
  },

  // Get selected instance IDs as array
  getSelectedIds: () => {
    return Array.from(get().selectedTrackIds);
  },

  // Add track instances to selection without clearing
  addToSelection: (instanceIds) => {
    const { selectedTrackIds } = get();
    const newSelection = new Set(selectedTrackIds);

    instanceIds.forEach((id) => newSelection.add(id));

    set({
      selectedTrackIds: newSelection,
      lastSelectedId: instanceIds[instanceIds.length - 1] ?? get().lastSelectedId,
    });
  },

  // Remove track instances from selection
  removeFromSelection: (instanceIds) => {
    const { selectedTrackIds } = get();
    const newSelection = new Set(selectedTrackIds);

    instanceIds.forEach((id) => newSelection.delete(id));

    set({
      selectedTrackIds: newSelection,
      lastSelectedId: newSelection.size > 0 ? get().lastSelectedId : null,
    });
  },

  // Invert selection across all track instances
  invertSelection: (allIds) => {
    const { selectedTrackIds } = get();
    const newSelection = new Set<string>();

    allIds.forEach((id) => {
      if (!selectedTrackIds.has(id)) {
        newSelection.add(id);
      }
    });

    set({
      selectedTrackIds: newSelection,
      lastSelectedId: newSelection.size > 0 ? allIds[allIds.length - 1] : null,
    });
  },
}));
